import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { workspaceKeys } from '@/hooks/api/query-keys';

// ============ Types ============

// One interface PER endpoint — /workspaces/my and /workspaces/current return
// different payloads and sharing a single `Workspace` type hid two live bugs:
// the current-workspace response has no `companyName`, and it returns the
// `locale` column under the key `language`, so every `.locale` read was
// permanently undefined.

/** GET /workspaces/my — membership list behind the workspace switcher. */
export interface Workspace {
  id: string;
  name: string;
  slug: string;
  role: string;
  companyId: string;
  companyName: string;
  /**
   * The logo the app header shows (useWorkspaceLogo): the workspace's own,
   * else its company's, as stored (a /uploads/ path or a URL); null = none.
   */
  logoUrl?: string | null;
  /** The company's own logo, for the All workspaces view; null = none. */
  companyLogoUrl?: string | null;
  /** What stands in for each on a dark surface: a dark logo the customer uploaded, else the white copy; null = the plate. */
  darkLogoUrl?: string | null;
  companyDarkLogoUrl?: string | null;
  /** The size the header draws them at (Brand Kit): its own, else its company's; and the company's. */
  logoSize?: string | null;
  companyLogoSize?: string | null;
}

/** GET /workspaces/current (and the PUT response) — CurrentWorkspaceResponseDto. */
export interface CurrentWorkspace {
  id: string;
  name: string;
  slug: string;
  status: string;
  timezone: string | null;
  /** The workspace's country, else its company's (ISO 3166-1 alpha-2). */
  country?: string | null;
  /** The workspaces.locale column — the API serialises it as `language`. */
  language: string | null;
  logoUrl: string | null;
  domains: {
    id: string;
    name: string;
    verified: boolean;
    primary: boolean;
  }[];
  createdAt: string;
}

export interface UpdateWorkspaceDto {
  name?: string;
  /** ISO 3166-1 alpha-2; null clears it. Moves no zone (country-first tenant creation). */
  country?: string | null;
  timezone?: string;
  /** Write key stays `locale` — UpdateCurrentWorkspaceDto expects the column name. */
  locale?: string;
  /**
   * The workspace logo: the /uploads/ path POST /onboarding/upload-logo
   * returned; null clears it (e-mail then uses the company logo, then a
   * neutral one).
   */
  logoUrl?: string | null;
  settings?: Record<string, unknown>;
}

// ============ Query Hooks ============

export function useWorkspaces() {
  return useQuery({
    queryKey: workspaceKeys.list(),
    queryFn: async () => {
      const result = await fetchApi<Workspace[] | { data: Workspace[] }>('/workspaces/my');
      // Normalize: backend returns flat array, but handle paginated shape too
      return Array.isArray(result) ? result : (result.data ?? []);
    },
  });
}

export function useCurrentWorkspace() {
  return useQuery({
    queryKey: [...workspaceKeys.all, 'current'] as const,
    queryFn: () => fetchApi<CurrentWorkspace>('/workspaces/current'),
    staleTime: Infinity,        // workspace rarely changes within a session
    gcTime: 1000 * 60 * 60,    // 1 hour
  });
}

/** GET /workspaces item: a company workspace the caller can reach. */
export interface CompanyWorkspace {
  id: string;
  name: string;
  /** Its own country, else its company's (ISO 3166-1 alpha-2). */
  country?: string | null;
}

/**
 * The company's workspaces the caller can reach (GET /workspaces, one page of
 * up to 500): the Organization page offers each one on the old country when
 * the company's country changes.
 */
export function useCompanyWorkspaces() {
  return useQuery({
    queryKey: [...workspaceKeys.all, 'company'] as const,
    queryFn: async () => {
      const result = await fetchApi<CompanyWorkspace[] | { data: CompanyWorkspace[] }>('/workspaces?limit=500');
      return Array.isArray(result) ? result : (result.data ?? []);
    },
  });
}

// ============ Mutation Hooks ============

/**
 * The company's country, applied to the ticked workspaces: ONE request, so one
 * audit row (#1917). `notUpdated` names every workspace that kept its country.
 */
export function useBulkWorkspaceCountry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids: string[]; country: string }) =>
      fetchApi<{ updated: number; ids: string[]; notUpdated: { id: string; errorCode: string }[] }>(
        '/workspaces/bulk-country',
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
}

export function useUpdateWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateWorkspaceDto) =>
      fetchApi<CurrentWorkspace>('/workspaces/current', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
}
